import { ChevronRight, ContactRound, Settings, Tag } from "lucide-react";
import { useEffect, useRef, useState } from "react";

import {
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { PAGE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import { cn } from "@/lib/utils/cn";
import { Link } from "@tanstack/react-router";

const CONTACT_SETTINGS_PATH = "/settings/contact";

const contactsSubNavLabelClass =
  "[&>span:last-child]:min-w-0 [&>span:last-child]:whitespace-nowrap [&>span:last-child]:overflow-hidden [&>span:last-child]:text-ellipsis";

export const ContactsSubMenu = ({ currentPath }: { currentPath: string }) => {
  const { setOpenMobile } = useSidebar();
  const { hasPagePermission } = useUser();
  const [settingsOpen, setSettingsOpen] = useState(() =>
    currentPath.startsWith(CONTACT_SETTINGS_PATH),
  );
  const prevPathRef = useRef(currentPath);

  useEffect(() => {
    const prev = prevPathRef.current;
    prevPathRef.current = currentPath;
    if (
      !prev.startsWith(CONTACT_SETTINGS_PATH) &&
      currentPath.startsWith(CONTACT_SETTINGS_PATH)
    ) {
      setSettingsOpen(true);
    }
  }, [currentPath]);

  const canViewAllContacts = hasPagePermission(
    PAGE_PERMISSION_KEYS.contactsAll,
  );
  const canViewContactTags = hasPagePermission(
    PAGE_PERMISSION_KEYS.contactsSettingsTags,
  );

  return (
    <SidebarMenuSub className="mt-1.5 mr-0 ml-2 gap-1 border-l-2 border-l-neutral-900 py-0.5 pr-0 pl-3">
      {canViewAllContacts ? (
        <SidebarMenuSubItem>
          <SidebarMenuSubButton asChild className={contactsSubNavLabelClass}>
            <Link
              to="/contacts"
              className={cn(
                currentPath.startsWith("/contacts") &&
                  "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700",
              )}
              onClick={() => {
                setOpenMobile(false);
              }}
            >
              <ContactRound className="mr-2 h-4 w-4 shrink-0" />
              All contacts
            </Link>
          </SidebarMenuSubButton>
        </SidebarMenuSubItem>
      ) : null}
      {canViewContactTags ? (
        <SidebarMenuSubItem className="list-none p-0">
          <SidebarMenuSubButton
            className={contactsSubNavLabelClass}
            onClick={() => {
              setSettingsOpen((open) => !open);
            }}
          >
            <Settings className="mr-2 h-4 w-4 shrink-0" />
            <span className="flex-1 truncate text-left">Settings</span>
            <ChevronRight
              className={cn(
                "h-4 w-4 shrink-0 transition-transform",
                settingsOpen && "rotate-90",
              )}
              aria-hidden
            />
          </SidebarMenuSubButton>
          {settingsOpen ? (
            <SidebarMenuSub className="mt-0.5 mr-0 ml-0 border-l-2 border-l-neutral-300 py-0.5 pr-0 pl-3">
              <SidebarMenuSubItem>
                <SidebarMenuSubButton
                  asChild
                  className={contactsSubNavLabelClass}
                >
                  <Link
                    to="/settings/contact"
                    className={cn(
                      currentPath === "/settings/contact" &&
                        "bg-neutral-900 text-white hover:bg-neutral-700 active:bg-neutral-700",
                    )}
                    onClick={() => {
                      setOpenMobile(false);
                    }}
                  >
                    <Tag className="mr-2 h-4 w-4 shrink-0" />
                    Tags
                  </Link>
                </SidebarMenuSubButton>
              </SidebarMenuSubItem>
            </SidebarMenuSub>
          ) : null}
        </SidebarMenuSubItem>
      ) : null}
    </SidebarMenuSub>
  );
};
